import React, { useState, useEffect } from 'react';
import { useDashboard, useFormatage } from '../hooks/useAgroshopHooks';
import { produitsService, offresService } from '../services/api';
import { getErrorMessage } from '../utils/errorFormatter';

export default function VendorDashboard() {
  const { stats, loading, erreur, derniereMAJ, recharger } = useDashboard();
  const { formatPrix, formatDate } = useFormatage();
  const [ajustementLoading, setAjustementLoading] = useState(false);
  const [ajustementErreur, setAjustementErreur] = useState(null);
  const [ajustementReussi, setAjustementReussi] = useState(null);
  const [expandedStock, setExpandedStock] = useState(null);
  const [formStock, setFormStock] = useState({});
  const [mesProduits, setMesProduits] = useState([]);
  const [produitsLoading, setProduitsLoading] = useState(true);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [showProduitForm, setShowProduitForm] = useState(false);
  const [produitForm, setProduitForm] = useState({});
  const [produitFormErreur, setProduitFormErreur] = useState(null);
  const [produitFormSucces, setProduitFormSucces] = useState(null);
  const [offres, setOffres] = useState([]);
  const [offresLoading, setOffresLoading] = useState(true);
  const [offreForm, setOffreForm] = useState({
    produit: '',
    reduction_pourcentage: 0,
    reduction_montant: 0,
    date_debut: '',
    date_fin: '',
    titre: 'Offre vendeur',
    description: '',
    actif: true,
  });
  const [offreErreur, setOffreErreur] = useState(null);
  const [offreSucces, setOffreSucces] = useState(null);

  const rendreStatusBadge = (statut) => {
    const normalized = (statut || '').toString().toLowerCase();
    if (normalized === 'vip') return <span className="ag-badge ag-badge--vip">VIP</span>;
    if (normalized === 'actif') return <span className="ag-badge ag-badge--actif">Actif</span>;
    if (normalized === 'nouveau') return <span className="ag-badge ag-badge--nouveau">Nouveau</span>;
    return <span className="ag-badge ag-badge--default">{statut || '—'}</span>;
  };

  const renderClientCell = (client) => {
    const initials = client.nom_complet
      ? client.nom_complet.split(' ').map((part) => part.charAt(0)).slice(0, 2).join('')
      : '';
    return (
      <div className="ag-table-client-cell">
        <div className="ag-table-avatar">{initials}</div>
        <div>
          <div className="ag-table-client-name">{client.nom_complet}</div>
          <div className="ag-table-client-meta">{Array.isArray(client.cultures_pratiquees) ? client.cultures_pratiquees.join(', ') : client.cultures_pratiquees || '—'}</div>
        </div>
      </div>
    );
  };

  const appelerClient = (client) => {
    const rawTelephone = client.telephone || client.telephone_secondaire || '';
    const telephone = rawTelephone.toString().trim().replace(/\s+/g, '').replace(/[^+0-9]/g, '');
    if (!telephone) return;
    window.location.href = `tel:${telephone}`;
  };

  const ajusterStock = async (produitId, quantite, motif) => {
    setAjustementLoading(true);
    setAjustementErreur(null);
    setAjustementReussi(null);
    try {
      await produitsService.ajusterStock(produitId, quantite, motif);
      setAjustementReussi('Stock ajusté avec succès.');
      setFormStock({});
      setExpandedStock(null);
      setTimeout(() => recharger(), 500);
    } catch (e) {
      setAjustementErreur(getErrorMessage(e));
    } finally {
      setAjustementLoading(false);
    }
  };

  const handleStockSubmit = (produitId, e) => {
    e.preventDefault();
    const quantite = parseInt(formStock[`qty_${produitId}`] || 0);
    const motif = formStock[`motif_${produitId}`] || 'Ajustement inventaire';
    if (quantite === 0) {
      setAjustementErreur('Veuillez entrer une quantité.');
      return;
    }
    ajusterStock(produitId, quantite, motif);
  };

  const chargerBoutique = async () => {
    setProduitsLoading(true);
    setOffresLoading(true);
    try {
      const [produitsData, offresData] = await Promise.all([
        produitsService.getVendorProducts(),
        offresService.getAll(),
      ]);
      setMesProduits(Array.isArray(produitsData) ? produitsData : produitsData?.results || []);
      setOffres(Array.isArray(offresData) ? offresData : offresData?.results || []);
    } catch (e) {
      console.error(e);
    } finally {
      setProduitsLoading(false);
      setOffresLoading(false);
    }
  };

  useEffect(() => {
    chargerBoutique();
  }, []);

  const fermerProduitForm = () => {
    setShowProduitForm(false);
    setSelectedProduit(null);
    setProduitForm({});
    setProduitFormErreur(null);
    setProduitFormSucces(null);
  };

  const ouvrirProduit = (produit = null) => {
    setShowProduitForm(true);
    if (!produit) {
      setSelectedProduit(null);
      setProduitForm({
        nom: '', description: '', description_courte: '', prix_unitaire: 0,
        prix_gros: 0, quantite_min_gros: 0, quantite_stock: 0,
        seuil_alerte: 10, en_vedette: false, nouveau: false, actif: true,
      });
      setProduitFormErreur(null);
      setProduitFormSucces(null);
      return;
    }
    setSelectedProduit(produit);
    setProduitForm({
      nom: produit.nom || '',
      description: produit.description || '',
      description_courte: produit.description_courte || '',
      prix_unitaire: produit.prix_unitaire || 0,
      prix_gros: produit.prix_gros || 0,
      quantite_min_gros: produit.quantite_min_gros || 0,
      quantite_stock: produit.quantite_stock || 0,
      seuil_alerte: produit.seuil_alerte || 10,
      en_vedette: produit.en_vedette || false,
      nouveau: produit.nouveau || false,
      actif: produit.actif || false,
    });
    setProduitFormErreur(null);
    setProduitFormSucces(null);
  };

  const handleProduitInput = (field, value) => {
    setProduitForm((prev) => ({ ...prev, [field]: value }));
  };

  const sauvegarderProduit = async (e) => {
    e.preventDefault();
    setProduitFormErreur(null);
    setProduitFormSucces(null);

    try {
      if (selectedProduit) {
        await produitsService.update(selectedProduit.id, produitForm);
        setProduitFormSucces('Produit mis à jour avec succès.');
      } else {
        await produitsService.create(produitForm);
        setProduitFormSucces('Produit créé avec succès.');
      }
      setTimeout(() => chargerBoutique(), 500);
    } catch (err) {
      setProduitFormErreur(getErrorMessage(err));
    }
  };

  const handleOffreInput = (field, value) => {
    setOffreForm((prev) => ({ ...prev, [field]: value }));
  };

  const creerOffre = async (e) => {
    e.preventDefault();
    setOffreErreur(null);
    setOffreSucces(null);
    try {
      await offresService.create(offreForm);
      setOffreSucces('Promotion créée avec succès.');
      setOffreForm({
        produit: '', reduction_pourcentage: 0,
        reduction_montant: 0, date_debut: '', date_fin: '',
        titre: 'Offre vendeur', description: '', actif: true,
      });
      setTimeout(() => chargerBoutique(), 500);
    } catch (err) {
      setOffreErreur(getErrorMessage(err));
    }
  };

  if (loading) return <div className="ag-container">Chargement de l'espace vendeur…</div>;
  if (erreur) return <div className="ag-container">Erreur : {erreur}</div>;

  const clientRappels = stats.clients_recents?.slice(0, 3) || [];
  const alertesStock = stats.alertes_stock || [];
  const recommandations = [
    {
      label: 'Catégorie la plus vendue',
      value: stats.ventes_par_categorie?.[0]?.categorie || 'Aucune donnée',
      note: stats.ventes_par_categorie?.[0] ? `${formatPrix(stats.ventes_par_categorie[0].chiffre)} ce mois` : '—',
      type: 'success',
    },
    {
      label: 'Région la plus active',
      value: stats.ventes_par_region?.[0]?.region || 'Aucune donnée',
      note: stats.ventes_par_region?.[0] ? `${formatPrix(stats.ventes_par_region[0].chiffre)} ce mois` : '—',
      type: 'warning',
    },
    {
      label: 'Alertes de stock',
      value: `${stats.produits_stock_faible} sous seuil`,
      note: `${stats.produits_rupture} en rupture`,
      type: stats.produits_rupture > 0 ? 'danger' : 'success',
    },
  ];

  return (
    <div className="ag-card">
      <h2>Tableau de bord vendeur</h2>
      <p>Suivez vos ventes, rappelez vos clients prioritaires et gérez votre inventaire.</p>

      <div className="ag-dashboard-grid">
        <div className="ag-dashboard-card">
          <strong>Mes ventes ce mois</strong>
          <div>{formatPrix(stats.ventes_mois)}</div>
          <div className="ag-card-sub">Chiffre d'affaires</div>
        </div>
        <div className="ag-dashboard-card">
          <strong>Clients servis</strong>
          <div>{stats.nb_clients_actifs}</div>
          <div className="ag-card-sub">Clients actifs 30j</div>
        </div>
        <div className="ag-dashboard-card">
          <strong>Commandes payées</strong>
          <div>{stats.nb_commandes_mois}</div>
          <div className="ag-card-sub">Ce mois</div>
        </div>
        <div className="ag-dashboard-card">
          <strong>Nouveaux clients</strong>
          <div>{stats.nb_nouveaux_clients}</div>
          <div className="ag-card-sub">30 derniers jours</div>
        </div>
      </div>

      <div className="ag-dashboard-grid">
        <div className="ag-dashboard-card">
          <strong>Payouts en attente</strong>
          <div>{formatPrix(stats.payouts_summary?.total_a_transferer || 0)}</div>
          <div className="ag-card-sub">Net à transférer</div>
          {stats.payouts_summary?.next_payout_date ? (
            <div className="ag-card-note">Prochain payout: {formatDate(stats.payouts_summary.next_payout_date)}</div>
          ) : (
            <div className="ag-card-note">Aucun payout planifié</div>
          )}
        </div>
        <div className="ag-dashboard-card">
          <strong>Payouts transférés 30j</strong>
          <div>{formatPrix(stats.payouts_summary?.transfere_30j || 0)}</div>
          <div className="ag-card-sub">Net reçu</div>
        </div>
        <div className="ag-dashboard-card">
          <strong>Prévision stock</strong>
          <div>{stats.stock_forecast?.length ? `${stats.stock_forecast.length} produits` : 'Pas de données'}</div>
          <div className="ag-card-sub">Risques d'épuisement</div>
        </div>
        <div className="ag-dashboard-card">
          <strong>Produits sous seuil</strong>
          <div>{stats.produits_stock_faible}</div>
          <div className="ag-card-sub">Alerte stock</div>
        </div>
      </div>

      <section className="ag-dashboard-section">
        <h3>Prévision de stock</h3>
        <p className="ag-muted">Produits qui risquent d'être épuisés en priorité, d'après le volume vendu sur les 30 derniers jours.</p>
        {stats.stock_forecast?.length ? (
          <div className="ag-dashboard-table-wrapper">
            <table className="ag-dashboard-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Stock dispo</th>
                  <th>Vente / jour</th>
                  <th>Jours restants</th>
                </tr>
              </thead>
              <tbody>
                {stats.stock_forecast.map((item) => (
                  <tr key={item.produit_id}>
                    <td>{item.nom || item.reference}</td>
                    <td>{item.stock_disponible}</td>
                    <td>{item.ventes_par_jour}</td>
                    <td>{item.jours_restants}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ag-alert ag-alert--info">Aucune prévision de stock disponible pour l'instant.</div>
        )}
      </section>

      <div className="ag-dashboard-grid">
        <section className="ag-dashboard-card">
          <h3 style={{ marginBottom: '14px' }}>Mes clients à rappeler</h3>
          {clientRappels.length === 0 ? (
            <p>Aucun client prioritaire.</p>
          ) : (
            <ul className="ag-user-list">
              {clientRappels.map((client) => (
                <li key={client.id} className="ag-user-item">
                  <div>
                    <strong>{client.nom_complet}</strong>
                    <div className="ag-muted">
                      {Array.isArray(client.cultures_pratiquees) ? client.cultures_pratiquees.join(', ') : client.cultures_pratiquees || '—'}
                      {client.region ? ` · ${client.region}` : ''}
                    </div>
                  </div>
                  <button
                    className="ag-btn-secondary"
                    type="button"
                    onClick={() => appelerClient(client)}
                    disabled={!client.telephone && !client.telephone_secondaire}
                  >
                    Appeler
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="ag-dashboard-card">
          <h3 style={{ marginBottom: '14px' }}>Recommandations</h3>
          <div className="ag-summary-data">
            {recommandations.map((item) => (
              <div key={item.label} className="ag-summary-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: 700 }}>{item.label}</span>
                <span>{item.value}</span>
                <span style={{ color: item.type === 'danger' ? '#e36b5a' : item.type === 'success' ? '#9be38b' : '#f6e8d6' }}>{item.note}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="ag-dashboard-section">
        <h3>Mes meilleures ventes du mois</h3>
        <div className="ag-dashboard-table-wrapper">
          <table className="ag-dashboard-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Produit</th>
                <th>Quantité</th>
                <th>Chiffre</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_produits?.length === 0 ? (
                <tr><td colSpan="4">Aucun produit vendu ce mois.</td></tr>
              ) : (
                stats.top_produits?.map((produit, index) => (
                  <tr key={`${produit.produit__reference}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{produit.produit__nom}</td>
                    <td>{produit.total_vendu ?? '—'}</td>
                    <td>{formatPrix(produit.chiffre)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {ajustementReussi && (
        <div className="ag-alert ag-alert--success" style={{ marginBottom: '20px' }}>
          ✓ {ajustementReussi}
        </div>
      )}

      {ajustementErreur && (
        <div className="ag-alert ag-alert--error" style={{ marginBottom: '20px' }}>
          ⚠ {ajustementErreur}
        </div>
      )}

      <section className="ag-dashboard-section">
        <h3>Gestion du stock</h3>
        {alertesStock.length === 0 ? (
          <div className="ag-alert">✓ Tous vos stocks sont normaux.</div>
        ) : (
          <div className="ag-dashboard-table-wrapper">
            <table className="ag-dashboard-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Stock actuel</th>
                  <th>Seuil d'alerte</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {alertesStock.map((produit) => (
                  <React.Fragment key={produit.id}>
                    <tr>
                      <td>{produit.nom}</td>
                      <td style={{ fontWeight: 700, color: produit.quantite_stock === 0 ? '#e36b5a' : '#f6e8d6' }}>
                        {produit.quantite_stock} unités
                      </td>
                      <td>{produit.seuil_alerte}</td>
                      <td>
                        <button
                          type="button"
                          className="ag-btn-secondary"
                          onClick={() => setExpandedStock(expandedStock === produit.id ? null : produit.id)}
                          disabled={ajustementLoading}
                        >
                          {expandedStock === produit.id ? 'Fermer' : 'Ajuster'}
                        </button>
                      </td>
                    </tr>
                    {expandedStock === produit.id && (
                      <tr>
                        <td colSpan="4" style={{ padding: '20px', backgroundColor: '#f9f7f4' }}>
                          <form onSubmit={(e) => handleStockSubmit(produit.id, e)}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                              <div style={{ flex: 1 }}>
                                <label>Quantité à ajouter/retirer</label>
                                <input
                                  type="number"
                                  value={formStock[`qty_${produit.id}`] || ''}
                                  onChange={(e) => setFormStock({ ...formStock, [`qty_${produit.id}`]: e.target.value })}
                                  placeholder="Ex: +50 ou -10"
                                  disabled={ajustementLoading}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label>Motif</label>
                                <input
                                  type="text"
                                  value={formStock[`motif_${produit.id}`] || ''}
                                  onChange={(e) => setFormStock({ ...formStock, [`motif_${produit.id}`]: e.target.value })}
                                  placeholder="Inventaire, retour client..."
                                  disabled={ajustementLoading}
                                />
                              </div>
                              <button
                                type="submit"
                                className="ag-btn ag-btn--primary"
                                disabled={ajustementLoading}
                              >
                                {ajustementLoading ? 'Chargement...' : 'Valider'}
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ag-dashboard-section">
        <h3>Gestion de la boutique</h3>
        {produitsLoading ? (
          <div className="ag-alert">Chargement des produits vendeur…</div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span>Votre catalogue</span>
              <button type="button" className="ag-btn ag-btn--secondary" onClick={() => ouvrirProduit(null)}>
                + Ajouter un produit
              </button>
            </div>
            {mesProduits.length === 0 ? (
              <div className="ag-alert ag-alert--info">Aucun produit dans votre catalogue pour l'instant.</div>
            ) : (
              <div className="ag-dashboard-table-wrapper">
                <table className="ag-dashboard-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Prix</th>
                      <th>Prix gros</th>
                      <th>Stock</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mesProduits.map((produit) => (
                      <tr key={produit.id}>
                        <td>{produit.nom}</td>
                        <td>{formatPrix(produit.prix_unitaire)}</td>
                        <td>{produit.prix_gros ? formatPrix(produit.prix_gros) : '—'}</td>
                        <td>{produit.quantite_stock}</td>
                        <td>
                          <button type="button" className="ag-btn-secondary" onClick={() => ouvrirProduit(produit)}>
                            Modifier
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showProduitForm && (
              <div style={{ marginTop: '24px' }}>
                <h4>{selectedProduit ? 'Modifier le produit' : 'Ajouter un produit'}</h4>
                {produitFormErreur && <div className="ag-alert ag-alert--error">{produitFormErreur}</div>}
                {produitFormSucces && <div className="ag-alert ag-alert--success">{produitFormSucces}</div>}
                <form onSubmit={sauvegarderProduit} style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <label>
                      Nom
                      <input type="text" value={produitForm.nom || ''} onChange={(e) => handleProduitInput('nom', e.target.value)} required />
                    </label>
                    <label>
                      Prix unitaire
                      <input type="number" value={produitForm.prix_unitaire || ''} onChange={(e) => handleProduitInput('prix_unitaire', Number(e.target.value))} required />
                    </label>
                    <label>
                      Prix gros
                      <input type="number" value={produitForm.prix_gros || ''} onChange={(e) => handleProduitInput('prix_gros', Number(e.target.value))} />
                    </label>
                    <label>
                      Qté min gros
                      <input type="number" value={produitForm.quantite_min_gros || ''} onChange={(e) => handleProduitInput('quantite_min_gros', Number(e.target.value))} />
                    </label>
                    <label>
                      Stock
                      <input type="number" value={produitForm.quantite_stock || ''} onChange={(e) => handleProduitInput('quantite_stock', Number(e.target.value))} />
                    </label>
                    <label>
                      Seuil d'alerte
                      <input type="number" value={produitForm.seuil_alerte || ''} onChange={(e) => handleProduitInput('seuil_alerte', Number(e.target.value))} />
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <label>
                      Description courte
                      <input type="text" value={produitForm.description_courte || ''} onChange={(e) => handleProduitInput('description_courte', e.target.value)} />
                    </label>
                    <label>
                      Actif
                      <select value={produitForm.actif ? '1' : '0'} onChange={(e) => handleProduitInput('actif', e.target.value === '1')}>
                        <option value="1">Oui</option>
                        <option value="0">Non</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    Description longue
                    <textarea value={produitForm.description || ''} onChange={(e) => handleProduitInput('description', e.target.value)} rows={4} />
                  </label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="submit" className="ag-btn ag-btn--primary">
                      {selectedProduit ? 'Enregistrer les modifications' : 'Créer le produit'}
                    </button>
                    <button type="button" className="ag-btn-secondary" onClick={fermerProduitForm}>
                      Fermer
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </section>

      <section className="ag-dashboard-section">
        <h3>Promotions</h3>
        {offresLoading ? (
          <div className="ag-alert">Chargement des promotions…</div>
        ) : (
          <>
            {offreErreur && <div className="ag-alert ag-alert--error">{offreErreur}</div>}
            {offreSucces && <div className="ag-alert ag-alert--success">{offreSucces}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <label>
                Produit
                <select value={offreForm.produit || ''} onChange={(e) => handleOffreInput('produit', e.target.value)} required>
                  <option value="">Sélectionner un produit</option>
                  {mesProduits.map((produit) => (
                    <option key={produit.id} value={produit.id}>{produit.nom}</option>
                  ))}
                </select>
              </label>
              <label>
                Réduction (%)
                <input type="number" value={offreForm.reduction_pourcentage || ''} onChange={(e) => handleOffreInput('reduction_pourcentage', Number(e.target.value))} />
              </label>
              <label>
                Réduction (montant FCFA)
                <input type="number" value={offreForm.reduction_montant || ''} onChange={(e) => handleOffreInput('reduction_montant', Number(e.target.value))} />
              </label>
              <label>
                Date début
                <input type="datetime-local" value={offreForm.date_debut || ''} onChange={(e) => handleOffreInput('date_debut', e.target.value)} required />
              </label>
              <label>
                Date fin
                <input type="datetime-local" value={offreForm.date_fin || ''} onChange={(e) => handleOffreInput('date_fin', e.target.value)} required />
              </label>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label>
                Titre de la promotion
                <input type="text" value={offreForm.titre || ''} onChange={(e) => handleOffreInput('titre', e.target.value)} />
              </label>
              <label>
                Description de la promotion
                <textarea value={offreForm.description || ''} onChange={(e) => handleOffreInput('description', e.target.value)} rows={3} />
              </label>
              <button type="button" className="ag-btn ag-btn--primary" onClick={creerOffre}>
                Créer la promotion
              </button>
            </div>
            {offres.length > 0 ? (
              <div className="ag-dashboard-table-wrapper" style={{ marginTop: '20px' }}>
                <table className="ag-dashboard-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Réduction</th>
                      <th>Période</th>
                      <th>Actif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offres.map((offre) => (
                      <tr key={offre.id}>
                        <td>{offre.produit_nom}</td>
                        <td>{offre.reduction_pourcentage ? `${offre.reduction_pourcentage}%` : `${offre.reduction_montant} FCFA`}</td>
                        <td>{offre.date_debut} → {offre.date_fin}</td>
                        <td>{offre.actif ? 'Oui' : 'Non'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ag-alert ag-alert--info" style={{ marginTop: '12px' }}>Aucune promotion active pour le moment.</div>
            )}
          </>
        )}
      </section>

      <section className="ag-dashboard-section">
        <div className="ag-dashboard-grid">
          <div className="ag-dashboard-card">
            <h3>Résumé stock</h3>
            <p>{stats.produits_stock_faible} produits sous seuil</p>
            <p>{stats.produits_rupture} produits en rupture</p>
          </div>
          <div className="ag-dashboard-card">
            <h3>Clients récents</h3>
            <ul className="ag-user-list">
              {stats.clients_recents?.slice(0, 5).map((client) => (
                <li key={client.id} className="ag-user-item">
                  <div>
                    <strong>{client.nom_complet}</strong>
                    <div className="ag-muted">{client.region || client.ville || '—'}</div>
                  </div>
                  {rendreStatusBadge(client.statut)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <footer className="ag-muted">Dernière mise à jour : {formatDate(derniereMAJ?.toISOString?.() || new Date().toISOString())}</footer>
    </div>
  );
}

